import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

// 17 sep 2026 (Augusto: "¿alguna manera de no depender de Cal.com?").
// Reemplaza el embed de Cal.com en /consultant por un calendario propio.
// Lo que se gana: el texto está en el idioma de la app (el evento de
// Cal.com estaba escrito en español y su embed no lo traducía, que fue
// justamente el problema que Augusto reportó), las solicitudes quedan en
// su propia base, y no hay un tercero de por medio.
//
// Sigue el mismo patrón que ACS (ver Notion, "ACS — Agenda, Clientes y
// Seguimiento"): la disponibilidad la calcula el backend a partir del
// horario de atención guardado, y esta pantalla solo la dibuja. La hora
// que llega ya viene en hora de Lima, y acá NO se convierte con
// toLocaleTimeString ni con el huso del navegador -- ese fue el bug más
// repetido de ACS (horas corridas según el dispositivo de quien mira).
// Las cadenas "HH:MM" se muestran tal cual llegan.

const MESES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Lunes primero, como se lee un calendario en Perú.
const DIAS_ES = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const DIAS_EN = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

// Las fechas llegan como "YYYY-MM-DD". Se parsean a mano y no con
// new Date(iso): esa forma las interpreta como UTC y, en Lima (UTC-5),
// corre el día una casilla hacia atrás.
const partesFecha = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
};

const ConsultaScheduler = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';

  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [diasDisponibles, setDiasDisponibles] = useState([]);
  const [mesOffset, setMesOffset] = useState(0);

  const [fechaElegida, setFechaElegida] = useState(null);
  const [horaElegida, setHoraElegida] = useState(null);

  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', mensaje: '' });
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [confirmado, setConfirmado] = useState(null);

  const cargarDisponibilidad = useCallback(() => {
    setCargando(true);
    apiClient
      .get('/consultations/availability')
      .then((res) => {
        setDiasDisponibles(res.data?.dias || []);
        setErrorCarga(false);
      })
      .catch(() => setErrorCarga(true))
      .finally(() => setCargando(false));
  }, []);

  useEffect(cargarDisponibilidad, [cargarDisponibilidad]);

  const mapaDias = useMemo(() => {
    const mapa = {};
    diasDisponibles.forEach((d) => { mapa[d.fecha] = d.horarios; });
    return mapa;
  }, [diasDisponibles]);

  // El mes que se está mostrando, a partir del mes actual + el offset de
  // las flechas.
  const hoy = new Date();
  const mesBase = new Date(hoy.getFullYear(), hoy.getMonth() + mesOffset, 1);
  const anio = mesBase.getFullYear();
  const mes = mesBase.getMonth();

  const celdas = useMemo(() => {
    const primero = new Date(anio, mes, 1);
    const diasEnMes = new Date(anio, mes + 1, 0).getDate();
    // getDay(): 0 = domingo. Se convierte a "lunes primero".
    const offsetInicial = (primero.getDay() + 6) % 7;
    const salida = [];
    for (let i = 0; i < offsetInicial; i += 1) salida.push(null);
    for (let d = 1; d <= diasEnMes; d += 1) {
      const iso = `${anio}-${String(mes + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      salida.push({ dia: d, iso, disponible: Boolean(mapaDias[iso]?.length) });
    }
    return salida;
  }, [anio, mes, mapaDias]);

  const horariosDelDia = fechaElegida ? mapaDias[fechaElegida] || [] : [];

  const elegirDia = (iso) => {
    setFechaElegida(iso);
    setHoraElegida(null);
    setErrorEnvio(null);
  };

  const puedeEnviar =
    fechaElegida && horaElegida && form.nombre.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email);

  const enviar = async () => {
    if (!puedeEnviar || enviando) return;
    setEnviando(true);
    setErrorEnvio(null);
    try {
      const res = await apiClient.post('/consultations/request', {
        nombre: form.nombre.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim() || null,
        mensaje: form.mensaje.trim() || null,
        fecha: fechaElegida,
        hora: horaElegida,
        idioma: language,
      });
      setConfirmado({ fecha: res.data.fecha, hora: res.data.hora });
    } catch (err) {
      const detalle = err?.response?.data?.detail;
      setErrorEnvio(
        detalle ||
          (isSpanish
            ? 'No se pudo enviar la solicitud. Inténtalo de nuevo.'
            : 'Could not send the request. Please try again.')
      );
      // Si el horario se lo llevó alguien más (409), la disponibilidad que
      // tiene en pantalla ya no es la real -- se recarga sola.
      if (err?.response?.status === 409) {
        cargarDisponibilidad();
        setHoraElegida(null);
      }
    } finally {
      setEnviando(false);
    }
  };

  const nombreFecha = (iso) => {
    const { y, m, d } = partesFecha(iso);
    const meses = isSpanish ? MESES_ES : MESES_EN;
    return isSpanish ? `${d} de ${meses[m - 1]} de ${y}` : `${meses[m - 1]} ${d}, ${y}`;
  };

  if (confirmado) {
    return (
      <Paper elevation={1} sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {isSpanish ? 'Solicitud enviada' : 'Request sent'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {isSpanish
            ? `Pediste el ${nombreFecha(confirmado.fecha)} a las ${confirmado.hora} (hora de Lima).`
            : `You requested ${nombreFecha(confirmado.fecha)} at ${confirmado.hora} (Lima time).`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isSpanish
            ? 'El especialista revisa cada solicitud a mano. Te llega un correo apenas quede confirmada — si ese horario no le funciona, te propone otro.'
            : 'The specialist reviews each request by hand. You will get an email as soon as it is confirmed — if that time does not work, he will propose another one.'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
      {cargando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {errorCarga && !cargando && (
        <Alert severity="error" action={<Button size="small" onClick={cargarDisponibilidad}>{isSpanish ? 'Reintentar' : 'Retry'}</Button>}>
          {isSpanish
            ? 'No se pudieron cargar los horarios disponibles.'
            : 'Could not load the available times.'}
        </Alert>
      )}

      {!cargando && !errorCarga && (
        <Grid container spacing={3}>
          {/* Calendario */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <IconButton size="small" onClick={() => setMesOffset((v) => v - 1)} disabled={mesOffset <= 0}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="subtitle1" fontWeight={600}>
                {(isSpanish ? MESES_ES : MESES_EN)[mes]} {anio}
              </Typography>
              <IconButton size="small" onClick={() => setMesOffset((v) => v + 1)} disabled={mesOffset >= 2}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {(isSpanish ? DIAS_ES : DIAS_EN).map((d, i) => (
                <Typography key={i} variant="caption" align="center" color="text.secondary">
                  {d}
                </Typography>
              ))}
              {celdas.map((celda, i) =>
                celda === null ? (
                  <Box key={`v-${i}`} />
                ) : (
                  <Button
                    key={celda.iso}
                    size="small"
                    disabled={!celda.disponible}
                    variant={fechaElegida === celda.iso ? 'contained' : 'text'}
                    onClick={() => elegirDia(celda.iso)}
                    sx={{
                      minWidth: 0,
                      p: 0.5,
                      borderRadius: 1,
                      fontWeight: celda.disponible ? 600 : 400,
                      bgcolor: fechaElegida === celda.iso ? undefined : celda.disponible ? 'action.hover' : undefined,
                    }}
                  >
                    {celda.dia}
                  </Button>
                )
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {isSpanish
                ? 'Los días resaltados tienen horarios libres. Todos los horarios están en hora de Lima (GMT-5). La sesión dura 60 minutos.'
                : 'Highlighted days have open times. All times are Lima time (GMT-5). The session lasts 60 minutes.'}
            </Typography>
          </Grid>

          {/* Horarios + formulario */}
          <Grid item xs={12} md={6}>
            {!fechaElegida && (
              <Typography variant="body2" color="text.secondary">
                {isSpanish ? 'Elige un día para ver los horarios.' : 'Pick a day to see the available times.'}
              </Typography>
            )}

            {fechaElegida && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  {nombreFecha(fechaElegida)}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {horariosDelDia.map((h) => (
                    <Chip
                      key={h}
                      label={h}
                      color={horaElegida === h ? 'primary' : 'default'}
                      variant={horaElegida === h ? 'filled' : 'outlined'}
                      onClick={() => setHoraElegida(h)}
                    />
                  ))}
                </Box>

                {horaElegida && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <TextField
                      fullWidth size="small" required sx={{ mb: 1.5 }}
                      label={isSpanish ? 'Nombre' : 'Name'}
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    />
                    <TextField
                      fullWidth size="small" required type="email" sx={{ mb: 1.5 }}
                      label={isSpanish ? 'Correo' : 'Email'}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      helperText={isSpanish ? 'Acá te llega la confirmación.' : 'The confirmation goes here.'}
                    />
                    <TextField
                      fullWidth size="small" sx={{ mb: 1.5 }}
                      label={isSpanish ? 'WhatsApp (opcional)' : 'WhatsApp (optional)'}
                      value={form.telefono}
                      onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    />
                    <TextField
                      fullWidth size="small" multiline rows={3} sx={{ mb: 2 }}
                      label={isSpanish ? '¿Qué quieres resolver? (opcional)' : 'What do you want to solve? (optional)'}
                      value={form.mensaje}
                      onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                    />

                    {errorEnvio && <Alert severity="error" sx={{ mb: 2 }}>{errorEnvio}</Alert>}

                    <Button
                      fullWidth variant="contained" size="large"
                      disabled={!puedeEnviar || enviando}
                      onClick={enviar}
                    >
                      {enviando
                        ? (isSpanish ? 'Enviando...' : 'Sending...')
                        : (isSpanish ? 'Solicitar este horario' : 'Request this time')}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {isSpanish
                        ? 'La cita queda confirmada cuando el especialista la aprueba. Te avisamos por correo.'
                        : 'The booking is confirmed once the specialist approves it. We will email you.'}
                    </Typography>
                  </>
                )}

                {horariosDelDia.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {isSpanish ? 'No quedan horarios ese día.' : 'No times left that day.'}
                  </Typography>
                )}
              </>
            )}
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default ConsultaScheduler;
